import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  TextField,
  Typography,
  List,
  ListItem,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";

import {
  createSource,
  updateSource,
  deleteSource,
} from "../../api/endpoints/enrollmentCrm";

const SourcesSettingsDialog = ({ open, onClose, sources = [], onSaved, isAdmin = false }) => {
  const [local, setLocal] = useState([]);
  const [newSource, setNewSource] = useState({ name: "", description: "" });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLocal([...(sources || [])]);
    setNewSource({ name: "", description: "" });
    setLoading(false);
  }, [open, sources]);

  const handleAdd = async () => {
    if (!newSource.name.trim()) return;
    setLoading(true);
    try {
      await createSource({
        name: newSource.name.trim(),
        description: newSource.description?.trim() || null,
      });
      if (onSaved) await onSaved();
      setNewSource({ name: "", description: "" });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (source) => {
    setLoading(true);
    try {
      await updateSource(source.id, {
        name: source.name,
        description: source.description || null,
      });
      if (onSaved) await onSaved();
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (sourceId) => {
    if (!window.confirm("Excluir fonte?")) return;
    setLoading(true);
    try {
      await deleteSource(sourceId);
      if (onSaved) await onSaved();
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="paper">
      <DialogTitle>Configurações de Fontes</DialogTitle>
      <DialogContent dividers>
        {!isAdmin ? (
          <Typography variant="body1" align="center" sx={{ py: 3 }}>
            Somente administradores podem acessar esta funcionalidade.
          </Typography>
        ) : (
          <Box>
            <Paper sx={{ p: 2, mb: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                Nova fonte
              </Typography>
              <TextField
                label="Nome"
                fullWidth
                margin="dense"
                value={newSource.name}
                onChange={(e) => setNewSource((p) => ({ ...p, name: e.target.value }))}
              />
              <TextField
                label="Descrição"
                fullWidth
                margin="dense"
                value={newSource.description}
                onChange={(e) => setNewSource((p) => ({ ...p, description: e.target.value }))}
              />
              <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
                <Button variant="contained" onClick={handleAdd} disabled={loading || !newSource.name.trim()}>
                  Adicionar
                </Button>
              </Box>
            </Paper>

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle1" gutterBottom>
              Lista de fontes
            </Typography>
            <List dense>
              {local.map((src) => (
                <ListItem key={src.id} disableGutters sx={{ px: 0, mb: 1 }}>
                  <Paper sx={{ p: 2, width: "100%" }}>
                    <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>
                      <TextField
                        label="Nome"
                        size="small"
                        value={src.name}
                        onChange={(e) =>
                          setLocal((prev) => prev.map((s) => (s.id === src.id ? { ...s, name: e.target.value } : s)))
                        }
                        sx={{ flex: 1 }}
                      />
                      <IconButton onClick={() => handleSave(src)} disabled={loading}>
                        <Typography sx={{ fontWeight: 700, fontSize: 12 }}>Salvar</Typography>
                      </IconButton>
                      <IconButton onClick={() => handleDelete(src.id)} disabled={loading} color="error">
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>

                    <TextField
                      label="Descrição"
                      size="small"
                      value={src.description || ""}
                      onChange={(e) =>
                        setLocal((prev) =>
                          prev.map((s) => (s.id === src.id ? { ...s, description: e.target.value } : s))
                        )
                      }
                      sx={{ mt: 1, width: "100%" }}
                    />
                  </Paper>
                </ListItem>
              ))}
              {local.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                  Nenhuma fonte cadastrada.
                </Typography>
              ) : null}
            </List>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit" disabled={loading}>
          Fechar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SourcesSettingsDialog;

